/* @flow */
/**
 * Contains PO search request helpers for expanding virtual fields, building filter metadata,
 * scheduler data, SIRE payloads, and sorted dropdown values.
 */
import {
  getFiltersInResponseRequestBody,
  checkForColumnData
} from '../../helpers/dataHelpers';
import poSearchFieldProperties from '../../constants/fieldProperties/poSearchFieldProperties';
import fieldMapping from '../../constants/fieldMappings/poSearchFieldMapping';

/**
 * Expands the manufacturing-code virtual field into manufacturing address fields needed by the API
 * response.
 */
export const setFieldMfgCode = (requestBody: Object) => {
  let updatedPayload = requestBody;
  let markerField = null;
  if (requestBody?.fields?.includes(fieldMapping.SUPPLIER_LOCATION_NAME)) {
    markerField = fieldMapping.SUPPLIER_LOCATION_NAME;
  } else if (requestBody?.fields?.includes(fieldMapping.MANUFACTURING_CODE)) {
    markerField = fieldMapping.MANUFACTURING_CODE;
  }

  if (markerField) {
    const fieldsData = [...requestBody.fields];
    const itemDetailsIndex = fieldsData.indexOf(markerField);
    // Insert expanded fields immediately after the UI marker field to preserve column order.
    fieldsData.splice(
      itemDetailsIndex + 1,
      0,
      fieldMapping.MANUFACTURING_ADDRESS1,
      fieldMapping.MANUFACTURING_ADDRESS2,
      fieldMapping.MANUFACTURING_ADDRESS3
    );
    updatedPayload = {
      ...requestBody,
      fields: fieldsData
    };
  }
  return updatedPayload;
};

/**
 * Replaces the Asian-size marker field with the country-specific size fields required in PO search
 * requests.
 */
export const setFieldAsiaSize = (requestBody: Object) => {
  let updatedPayload = requestBody;
  if (requestBody?.fields?.includes('poLine.asianSpecificSizeIsRelevant')) {
    const fieldsData = [...requestBody.fields];
    const itemDetailsIndex = fieldsData.indexOf('poLine.asianSpecificSizeIsRelevant');
    fieldsData.splice(
      itemDetailsIndex + 1,
      0,
      'sizes.sizeProduct.kr.chestSizeCode',
      'sizes.sizeProduct.kr.waistSizeCode',
      'sizes.sizeProduct.kr.ageBySizeCode',
      'sizes.sizeProduct.kr.alternateSizeCode',
      'sizes.sizeProduct.cn.topSizeCode',
      'sizes.sizeProduct.cn.bottomSizeCode',
      'sizes.sizeProduct.cn.alternateSizeCode'
    );
    updatedPayload = {
      ...requestBody,
      // Remove the marker because the API expects concrete regional size fields.
      fields: fieldsData.filter((item) => item !== 'poLine.asianSpecificSizeIsRelevant')
    };
  }
  return updatedPayload;
};

/**
 * Expands the ship-to-address marker field into address component fields for report requests.
 */
export const setFieldShipToAddress = (requestBody: Object) => {
  let updatedPayload = requestBody;
  if (requestBody?.fields?.includes('poLine.shipping.address')) {
    const fieldsData = [...requestBody.fields].filter(
      (field) => ![
        'poLine.shipping.address.addressName1',
        'poLine.shipping.address.addressLine1Text',
        'poLine.shipping.address.cityName',
        'poLine.shipping.address.countryName',
        'poLine.shipping.address.postalCode'
      ].includes(field)
    );
    const itemDetailsIndex = fieldsData.indexOf('poLine.shipping.address');
    fieldsData.splice(
      itemDetailsIndex + 1,
      0,
      'poLine.shipping.address.addressName1',
      'poLine.shipping.address.addressLine1Text',
      'poLine.shipping.address.cityName',
      'poLine.shipping.address.countryName',
      'poLine.shipping.address.postalCode'
    );
    updatedPayload = {
      ...requestBody,
      fields: fieldsData.filter((item) => item !== 'poLine.shipping.address')
    };
  }
  return updatedPayload;
};

/**
 * Collapses expanded Asian-size API fields back to the UI marker field for persisted request state.
 */
export const removeFieldAsiaSize = (requestBody: Object) => {
  let updatedPayload = requestBody;
  if (requestBody?.fields?.includes('sizes.sizeProduct.kr.chestSizeCode')) {
    const fieldsData = [...requestBody.fields];
    const itemDetailsIndex = fieldsData.indexOf('sizes.sizeProduct.kr.chestSizeCode');
    fieldsData.splice(
      itemDetailsIndex + 1,
      0,
      'poLine.asianSpecificSizeIsRelevant'
    );
    updatedPayload = {
      ...requestBody,
      fields: fieldsData.filter(
        (field) => ![
          'sizes.sizeProduct.kr.chestSizeCode',
          'sizes.sizeProduct.kr.waistSizeCode',
          'sizes.sizeProduct.kr.ageBySizeCode',
          'sizes.sizeProduct.kr.alternateSizeCode',
          'sizes.sizeProduct.cn.topSizeCode',
          'sizes.sizeProduct.cn.bottomSizeCode',
          'sizes.sizeProduct.cn.alternateSizeCode'
        ].includes(field)
      )
    };
  }
  return updatedPayload;
};
/**
 * Removes manufacturing address helper fields before storing request state that should reflect
 * UI-selected columns.
 */
export const removeFieldMfgCode = (requestBody: Object) => {
  let updatedPayload = requestBody;
  if (requestBody?.fields?.includes(fieldMapping.MANUFACTURING_ADDRESS1)) {
    const fieldsData = [...requestBody.fields];
    updatedPayload = {
      ...requestBody,
      fields: fieldsData.filter(
        (field) => ![
          fieldMapping.MANUFACTURING_ADDRESS1,
          fieldMapping.MANUFACTURING_ADDRESS2,
          fieldMapping.MANUFACTURING_ADDRESS3
        ].includes(field)
      )
    };
  }
  return updatedPayload;
};

/**
 * Collapses expanded ship-to-address fields back to the single UI marker field.
 */
export const removeFieldShipToAddress = (requestBody: Object) => {
  let updatedPayload = requestBody;
  if (requestBody?.fields?.includes('poLine.shipping.address.addressName1')) {
    const fieldsData = [...requestBody.fields];
    const itemDetailsIndex = fieldsData.indexOf('poLine.shipping.address.addressName1');
    fieldsData.splice(
      itemDetailsIndex + 1,
      0,
      'poLine.shipping.address'
    );
    updatedPayload = {
      ...requestBody,
      fields: fieldsData.filter(
        (field) => ![
          'poLine.shipping.address.addressName1',
          'poLine.shipping.address.addressLine1Text',
          'poLine.shipping.address.cityName',
          'poLine.shipping.address.countryName',
          'poLine.shipping.address.postalCode'
        ].includes(field)
      )
    };
  }
  return updatedPayload;
};

/**
 * Ensures source-system name is included in fields so downstream row-level actions can identify
 * the source system.
 */
export const setFieldSrcSysNm = (requestBody: Object) => {
  let updatedPayload = requestBody;
  if (requestBody?.fields && !requestBody?.fields.includes('srcSysNm')) {
    updatedPayload = { ...requestBody, fields: [...requestBody.fields, 'srcSysNm'] };
  }
  return updatedPayload;
};
/**
 * Chooses customized column-order fields when available, otherwise falls back to default request
 * fields.
 */
export const getFieldsForRequest = (
  state: Object,
  selectedColumnOrderOptions: Object,
  fields: Object
) => (checkForColumnData(state, true) ? selectedColumnOrderOptions : fields);

/**
 * Maps selected filter keys to API filter-response descriptors using PO search field properties.
 */
export const getSelectedFilterValues = (filterValues: Object) => filterValues?.map((filter) => {
  const filterInResponse = {};
  const fieldProperties = poSearchFieldProperties[filter] || {};
  if (!fieldProperties.primary) {
    return null;
  }
  filterInResponse.primary = fieldProperties.primary;
  if (fieldProperties.secondary) {
    filterInResponse.secondary = fieldProperties.secondary;
  }
  return filterInResponse;
});
/**
 * Mutates the selected-filter map to add newly selected filters or clear removed filters.
 */
export const getModifiedSelectedFilters = (
  filtersSelected: Object,
  filter: string,
  modifiedSelectedFilters: Object
) => {
  const modifiedSelectedFiltersNew = modifiedSelectedFilters;
  if (filtersSelected.includes(filter)) {
    if (!modifiedSelectedFilters[filter]) modifiedSelectedFiltersNew[filter] = [];
  } else {
    modifiedSelectedFiltersNew[filter] = null;
  }
};
/**
 * Adds filter-response descriptors for selected filters and queues missing dropdown values for
 * retrieval.
 */
export function getSelectedFilterKeys(
  filter: string,
  modifiedSelectedFilters: Object,
  filterDropdownValues: Object,
  searchFiltersToBeRequested: Object,
  filtersInResponse: Object
) {
  // API filter descriptors can include a secondary field for display labels.
  const filterRequest: { primary: string; secondary?: string; } = { primary: filter };
  if (poSearchFieldProperties[filter]?.secondary) {
    filterRequest.secondary = poSearchFieldProperties[filter].secondary;
  }
  if (modifiedSelectedFilters[filter]) {
    if (!filterDropdownValues[filter]) {
      searchFiltersToBeRequested.push(filterRequest);
    }
    filtersInResponse.push(filterRequest);
  }
}

/**
 * Converts selected PO filter keys into API filtersInResponse entries and removes invalid mappings.
 */
export const getFilterInResponseValueInPOSearch = (selectedFilterKeys: Object) => (
  selectedFilterKeys.map((filter) => {
    const filterInResponse = {};
    const fieldProperties = poSearchFieldProperties[filter] || {};
    if (!fieldProperties) {
      return null;
    }
    filterInResponse.primary = fieldProperties.primary;
    if (fieldProperties.secondary) {
      filterInResponse.secondary = fieldProperties.secondary;
    }
    return filterInResponse;
  })
    .filter((element) => element)
);

/**
 * Returns user-selected filter-response fields or defaults from current PO selected-filter state.
 */
export const getFiltersInResponse = (selectedFilterOptions: Object,
  filterValuesOptByUser: Object,
  state: Object) => (selectedFilterOptions?.length
  ? filterValuesOptByUser
  : getFiltersInResponseRequestBody(state.PoSearch.selectedFilters));

/**
 * Deduplicates filtersInResponse entries by primary field while preserving the first matching
 * descriptor.
 */
export function getFilterData(filtersInResponse: Array<Object>): Array<Object> {
  return [...new Set(filtersInResponse.map(({ primary }: { primary: string }) => primary))]
    .map((primary: string) => filtersInResponse.find(
      ({ primary: p }: { primary: string }) => p === primary
    ));
}
/**
 * Merges scheduler response objects into saved-search rows keyed by saved-search ID.
 */
export const setSchedulerData = (
  schedulerResponse: Object,
  savedSearchData: Object
) => {
  // Saved-search IDs are used as array keys to allow fast scheduler lookup during merge.
  const schedulerData = [];
  schedulerResponse.data.objects.forEach((item) => {
    const { saveSearchID } = item;
    if (item?.executeOnce) {
      schedulerData[saveSearchID] = {
        id: item.id,
        saveSearchID: item.saveSearchID,
        scheduleID: item.scheduleID,
        startTime: item.startTime,
        state: item.state
      };
    } else {
      schedulerData[saveSearchID] = item;
    }
  });
  const newObj = savedSearchData.data.objects.map((item) => {
    const savedSearchItem = item;
    if (schedulerData[item.id]) {
      savedSearchItem.schedulerData = schedulerData[item.id];
    }
    return savedSearchItem;
  });
  return newObj;
};
/**
 * Maps a flattened PO search row into the compact SIRE API row payload.
 */
const mapFuncToRequest = (row: Object) => {
  const rowData = {
    poNumber: row[fieldMapping.PURCHASE_ORDER_NUMBER],
    itemNumber: row[fieldMapping.PO_LINE_ITEM_NUMBER],
    scheduleLineItemNumber: row[fieldMapping.SCHEDULE_LINE_ITEM_NUMBER],
    srcSysNm: row[fieldMapping.SRC_SYS_NM]
  };

  return rowData;
};

/**
 * Builds the SIRE request body from selected PO search rows.
 */
export const poSireRequestBody = (selectedRows: Object) => ({
  poData: selectedRows.map((row: Object) => mapFuncToRequest(row))
});

/**
 * Sorts filter dropdown arrays with field-specific ordering rules for country, document type, and
 * GAC reason codes.
 */
export const sortDataAscending = (data: Object) => {
  const sortedData = {};
  Object.keys(data).forEach((key) => {
    if (Array.isArray(data[key])) {
      // Country filters are sorted by their leading country code rather than the full label.
      if (key === 'poLine.destinationCountryCode') {
        sortedData[key] = [...data[key]].sort((a, b) => {
          const countryCodeA = (a.match(/^[^\s]+/) || [])[0];
          const countryCodeB = (b.match(/^[^\s]+/) || [])[0];
          if (!countryCodeA || !countryCodeB) {
            throw new Error('Invalid country code format');
          }
          if (countryCodeA < countryCodeB) return -1;
          if (countryCodeA > countryCodeB) return 1;
          return 0;
        });
      // Document-type filters sort alpha prefixes before their numeric suffixes.
      } else if (key === 'poHeader.poDocTypeCode') {
        sortedData[key] = [...data[key]].sort((a, b) => {
          const alphaA = a.match(/[a-zA-Z]+/)[0];
          const alphaB = b.match(/[a-zA-Z]+/)[0];
          const numA = Number.parseInt(a.match(/\d+/)[0], 10);
          const numB = Number.parseInt(b.match(/\d+/)[0], 10);
          if (alphaA === alphaB) {
            return numA - numB;
          }
          if (alphaA < alphaB) return -1;
          if (alphaA > alphaB) return 1;
          return 0;
        });
      // GAC reason-code filters sort by the leading numeric code.
      } else if (key === 'sizes.sizePo.goodsAtConsolidatorReasonCode') {
        sortedData[key] = [...data[key]].sort((a, b) => {
          const numA = Number.parseInt((a.match(/^\d+/) || [])[0], 10);
          const numB = Number.parseInt((b.match(/^\d+/) || [])[0], 10);
          return numA - numB;
        });
      } else {
        sortedData[key] = [...data[key]].sort((a, b) => {
          const valueA = String(a).toLowerCase();
          const valueB = String(b).toLowerCase();
          if (valueA < valueB) return -1;
          if (valueA > valueB) return 1;
          return 0;
        });
      }
    } else {
      sortedData[key] = [...data[key]];
    }
  });
  return sortedData;
};
